import { useState, useEffect, useRef, useCallback } from 'react'

export interface CameraDevice {
  deviceId: string
  label: string
}

export interface CameraState {
  availableCameras: CameraDevice[]
  isCameraRunning: boolean
  isLoading: boolean
}

export interface CameraActions {
  startCamera: (videoConstraints?: MediaTrackConstraints) => Promise<MediaStream>
  stopCamera: () => void
}

export const getCamerasList = async () => {
  // First try to get devices without permissions
  const devices = await navigator.mediaDevices.enumerateDevices()
  const videoDevices = devices
    .filter(device => device.kind === 'videoinput')
    .map(device => ({
      deviceId: device.deviceId,
      label: device.label || '',
    }))

  return videoDevices
}

export const useCamera = (): CameraState & CameraActions => {
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([])
  const [isCameraRunning, setIsCameraRunning] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const currentStreamRef = useRef<MediaStream | null>(null)

  const stopCamera = useCallback(() => {
    if (currentStreamRef.current) {
      currentStreamRef.current.getTracks().forEach(track => track.stop())
      currentStreamRef.current = null
    }
    setIsCameraRunning(false)
  }, [])

  // Get cameras on component mount only
  useEffect(() => {
    const fetchCamerasList = async () => {
      let videoDevices = await getCamerasList()
      // If we have cameras but no labels, try to get permissions to get proper labels
      if (videoDevices.length > 0 && videoDevices.some(device => !device.label)) {
        // Request minimal permissions to get device labels
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: videoDevices[0].deviceId },
        })
        // Stop the stream immediately
        stream.getTracks().forEach(track => track.stop())

        // Re-enumerate to get proper labels
        videoDevices = await getCamerasList()
      }

      setAvailableCameras(videoDevices)
    }

    setIsLoading(true)
    fetchCamerasList().finally(() => {
      setIsLoading(false)
    })

    // Cleanup stream on unmount
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  const startCamera = useCallback(async (videoConstraints: MediaTrackConstraints = {}) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        ...videoConstraints,
      },
    })

    currentStreamRef.current = stream
    setIsCameraRunning(true)

    return stream
  }, [])

  return {
    // State
    availableCameras,
    isCameraRunning,
    isLoading,

    // Actions
    startCamera,
    stopCamera,
  }
}
